import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { LAYOUT_MENU_PROPS } from "./configure/layoutMenuStyles";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import { formatName } from "../../../helpers/helpers";
import { PurpleSwitch } from "../../../helpers/helpers";

function LogbookCategoryDrawer(props) {
  const selectedMembers = (props.chatUsers || []).filter((user) =>
    (props.modalData.notify_user_ids || []).some(
      (id) => String(id) === String(user.id)
    )
  );
  const notificationsEnabled = Boolean(
    props.modalData.notify_users_enabled ||
      props.modalData.notify_user_ids?.length
  );
  const [inputValue, setInputValue] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const savingRef = React.useRef(false);

  useEffect(() => {
    if (props.open) {
      setSaveError("");
      setInputValue("");
    }
  }, [props.open]);

  const closeDrawer = () => {
    if (!savingRef.current) props.onClose();
  };

  const saveCategory = async () => {
    if (savingRef.current || !props.modalData.name?.trim()) return;
    savingRef.current = true;
    setIsSaving(true);
    setSaveError("");
    try {
      const saved = await props.post_logbook_category();
      if (saved) props.onClose();
    } catch (error) {
      setSaveError(
        error.message || "Could not save category. Please try again."
      );
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    props.setModalData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSwitchChange = (event) => {
    const checked = event.target.checked;
    props.setModalData((prev) => ({
      ...prev,
      notify_users_enabled: checked,
    }));
    if (!checked) {
      props.setModalData((prev) => ({
        ...prev,
        notify_user_ids: [],
      }));
    }
  };

  const handleMembersChange = (event, newValue) => {
    props.setModalData((prev) => ({
      ...prev,
      notify_user_ids: newValue.map((m) => m.id),
    }));
  };

  return (
    <Drawer anchor="right" open={props.open} onClose={closeDrawer}>
      <div className="w-[450px] max-w-[100vw] h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <div className="flex items-center justify-between px-6 py-8">
            <h2 className="text-slate-900 text-2xl">
              {props.modalEdit ? "Edit category" : "Create category"}
            </h2>
            <button
              aria-label="Close category drawer"
              disabled={isSaving}
              onClick={closeDrawer}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-40"
            >
              <CloseIcon />
            </button>
          </div>
          <fieldset disabled={isSaving} className="min-w-0 w-full px-6">
            <p className="text-slate-500 text-sm -mt-4">
              {props.modalEdit
                ? "Category details save immediately across all stores."
                : "Create a category, then save the layout to publish its placement."}
            </p>
            <div className="mt-7">
              <p className="text-sm text-slate-500">Name</p>
              <div className="mt-1">
                <TextField
                  placeholder="e.g. Shift handoff"
                  autoFocus
                  inputProps={{ "aria-label": "Category name" }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="name"
                  value={props.modalData.name}
                  onChange={handleChange}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      padding: "5px",
                      height: "45px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="mt-7">
              <p className="text-sm text-slate-500">Description (Optional)</p>
              <div className="mt-1">
                <TextField
                  placeholder="Help users understand what to log here..."
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="description"
                  value={props.modalData.description || ""}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      padding: "8px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                  inputProps={{
                    maxLength: 500,
                    "aria-label": "Category description",
                  }}
                />
                <p className="text-xs text-slate-400 mt-1">
                  {props.modalData.description?.length || 0}/500 characters
                </p>
              </div>
            </div>
            <div className="mt-7">
              <p className="text-slate-500 text-sm">Type</p>
              <div className="mt-1">
                <Select
                  MenuProps={LAYOUT_MENU_PROPS}
                  fullWidth
                  inputProps={{ "aria-label": "Category type" }}
                  disabled={isSaving}
                  value={props.modalData.type}
                  name="type"
                  onChange={handleChange}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    padding: "5px",
                    height: "45px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  }}
                >
                  {["text", "number", "star", "dollar", "percentage"].map(
                    (option, index) => (
                      <MenuItem key={index} value={option}>
                        {formatName(option)}
                      </MenuItem>
                    )
                  )}
                </Select>
              </div>
            </div>
            <div className="mt-7">
              <div className="flex items-center justify-between">
                <p className="text-slate-500 text-sm">
                  Do you want submissions of this logbook category to notify
                  specific people?
                </p>
                <div className="ml-4">
                  <PurpleSwitch
                    id="notify-users-enabled"
                    name="notify_users_enabled"
                    checked={notificationsEnabled}
                    disabled={isSaving}
                    onChange={handleSwitchChange}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleSwitchChange({
                          target: {
                            name: "notify_users_enabled",
                            checked: !notificationsEnabled,
                          },
                        });
                      }
                    }}
                  />
                </div>
              </div>
              {notificationsEnabled && (
                <div className="mt-2">
                  <Autocomplete
                    multiple
                    fullWidth
                    value={selectedMembers}
                    onChange={handleMembersChange}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => {
                      setInputValue(newInputValue);
                    }}
                    disableClearable
                    disableCloseOnSelect
                    options={props.chatUsers || []}
                    disabled={isSaving}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    getOptionLabel={(option) => formatName(option.name)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select members to notify"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px !important",
                            minHeight: "45px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                            backgroundColor: "white",
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}>
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={selected}
                          sx={{
                            color: "#6d28d9",
                            "&.Mui-checked": {
                              color: "#6d28d9",
                            },
                          }}
                        />
                        {formatName(option.name)}
                      </li>
                    )}
                  />
                </div>
              )}
            </div>
          </fieldset>
          <div className="absolute bottom-0 w-full bg-white border-t border-slate-200 px-6 py-4">
            {saveError && (
              <p
                role="alert"
                className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-700"
              >
                {saveError}
              </p>
            )}
            <div className="w-full">
              <LoadingButton
                fullWidth
                loading={isSaving}
                disabled={!props.modalData.name?.trim()}
                loadingPosition="end"
                endIcon={<SaveIcon />}
                variant="contained"
                onClick={saveCategory}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  paddingY: "14px",
                  backgroundColor: "#6d28d9",
                  "&:hover": {
                    backgroundColor: "#5b21b6",
                  },
                  fontSize: "16px",
                }}
              >
                {props.modalEdit ? "Save category" : "Create category"}
              </LoadingButton>
            </div>
            <div className="w-full mt-3">
              <button
                disabled={isSaving}
                onClick={closeDrawer}
                className="w-full py-3 rounded-xl border border-violet-700 bg-white text-violet-700 hover:bg-violet-50 focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </div>
          <div className="h-[280px]"></div>
        </div>
      </div>
    </Drawer>
  );
}

export default LogbookCategoryDrawer;
